import * as React from "react"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import { createUseStyles, useTheme } from "react-jss"
import { useAppSelector } from "../../util/hooks"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { freatureDriftDataSelector } from "./DataMetaDataSlice"
import {
  IFeatureDriftTableData,
  IFeatureDriftTableColumn,
} from "../../util/interface"
import { ABBREVIATE_NUMBER } from "../../util/CommonMethods"

const useStyles = createUseStyles({
  wrapper: {
    // width: "100%",
  },
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
  },
  borderRight: {
    borderRight: "1px solid lightgray",
  },
  borderLeft: {
    borderLeft: "1px solid lightgray",
  },
  borderBottom: {
    borderBottomColor: "transparent !important",
  },
  heading: {
    fontSize: 16,
    fontWeight: 600,
  },
  subHeading: {
    color: "#606060",
    fontSize: 14,
    fontWeight: 600,
  },
})

const colBorder = [0, 1, 2, 3, 4, 5, 6, 7, 8]

const ResultDiv = styled.div`
  height: 25px;
  width: 70px;
  display: inline-block;
  padding: 2px 10px;
  border-radius: 5px;
  margin: 5px;
`

const PassDiv = styled(ResultDiv)`
  background-color: #27af8d;
  color: white;
`

const FailedDiv = styled(ResultDiv)`
  background-color: #fcc;
  font-size: "14px";
  color: #da1818;
`

const columns: IFeatureDriftTableColumn[] = [
  {
    id: "YEAR_MONTH",
    label: "Year Month",
    minWidth: 100,
    align: "left",
  },
  {
    id: "CHANNEL",
    label: "Channel",
    minWidth: 100,
    align: "center",
  },
  {
    id: "FEATURE",
    label: "Feature",
    minWidth: 170,
    align: "left",
  },
  {
    id: "MONTHLY_SUM",
    label: "Monthly Sum",
    minWidth: 100,
    align: "center",
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "LAST_12_MONTH_AVG",
    label: "Last 12 Month AVG",
    minWidth: 130,
    align: "center",
  },
  {
    id: "PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG",
    label: "Percentage Change From Last 12 Months AVG",
    minWidth: 190,
    align: "center",
  },
  {
    id: "WARNING_THRESHOLD",
    label: "Warning Threshold",
    minWidth: 190,
    align: "center",
  },
]

function createData(
  YEAR_MONTH: string,
  CHANNEL: string,
  FEATURE: string,
  MONTHLY_SUM: number,
  LAST_12_MONTH_AVG: string,
  PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG: number,
  WARNING_THRESHOLD: number,
  highlight?: boolean,
): IFeatureDriftTableData {
  return {
    YEAR_MONTH,
    CHANNEL,
    FEATURE,
    MONTHLY_SUM,
    LAST_12_MONTH_AVG,
    PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG,
    WARNING_THRESHOLD,
    highlight,
  }
}

const TableHeader: React.FC<{ classes: any }> = ({ classes }) => (
  <TableHead className={classes.root}>
    <TableRow>
      <TableCell
        key={"id"}
        align={"center"}
        sx={{ borderRight: "1px solid lightgray" }}
      >
        <Typography variant="tableHeader"> S.No </Typography>
      </TableCell>
      {columns.map((column, i) => (
        <TableCell
          key={column.id}
          align={column.align}
          className={classes.wrapper}
          sx={{
            minWidth: column.minWidth,
            borderRight: colBorder.includes(i) ? "1px solid lightgray" : "",
          }}
        >
          <Typography variant="tableHeader"> {column.label} </Typography>
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
)

const formatCellValue = (column: any, value: any, classes: any) => {
  if (column.id === "MATCH_PERCENTAGE") {
    return (
      <div className={classes.matchPercentage}>
        {column.format && typeof value === "number"
          ? column.format(value)
          : value}
      </div>
    )
  }
  if (column.id === "ACCEPTABILITY_CRITERIA") {
    return (
      <div className={classes.acceptabilityCriteria}>
        {column.format && typeof value === "number"
          ? column.format(value)
          : value}
      </div>
    )
  }
  if (column.format && typeof value === "number") {
    return column.format(value)
  }
  if (value === "PASS") {
    return <PassDiv>Pass</PassDiv>
  }
  if (value === "FAIL") {
    return <FailedDiv>Failed</FailedDiv>
  }
  return value
}

const TableCellComponent = ({ column, value, classes, i }: any) => (
  <TableCell
    className={classes.wrapper}
    key={column.id}
    align={column.align}
    sx={{
      borderRight: colBorder.includes(i) ? "1px solid lightgray" : "",
      whiteSpace: "nowrap",
    }}
  >
    {formatCellValue(column, value, classes)}
  </TableCell>
)

const TableRowComponent = ({ row, index, page, rowsPerPage, classes }: any) => (
  <TableRow
    hover
    role="checkbox"
    sx={{
      background: row.highlight ? "#FBF68D99" : "inherit",
      "&:hover": {
        background: row.highlight ? "#FBF68D99 !important" : "inherit",
      },
    }}
    tabIndex={-1}
    key={index}
  >
    <TableCell
      align={"left"}
      sx={{ borderRight: "1px solid lightgray", minWidth: 2 }}
    >
      {page * rowsPerPage + index + 1}
    </TableCell>
    {columns.map((column, i) => (
      <TableCellComponent
        key={column.id}
        column={column}
        value={row[column.id]}
        classes={classes}
        i={i}
      />
    ))}
  </TableRow>
)

const TableRows: React.FC<{
  rows: IFeatureDriftTableData[]
  page: number
  rowsPerPage: number
  classes: any
}> = ({ rows, page, rowsPerPage, classes }) => (
  <TableBody>
    {rows
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row, index) => (
        <TableRowComponent
          key={index}
          row={row}
          index={index}
          page={page}
          rowsPerPage={rowsPerPage}
          classes={classes}
        />
      ))}
  </TableBody>
)

export default function FeatureDriftTable() {
  const [page, setPage] = React.useState(0) // Current page in pagination
  const [rowsPerPage, setRowsPerPage] = React.useState(10) // Rows to display per page
  const theme: any = useTheme()
  const classes = useStyles({ theme })

  const data: IFeatureDriftTableData[] = useAppSelector(
    freatureDriftDataSelector,
  )
  const [rows, setRows] = React.useState<IFeatureDriftTableData[]>([]) // State to hold transformed rows

  // Transform incoming data into the format required for the table
  React.useEffect(() => {
    const temp: IFeatureDriftTableData[] = data.map((row) =>
      createData(
        row["YEAR_MONTH"],
        row["CHANNEL"],
        row["FEATURE"],
        row["MONTHLY_SUM"],
        row["LAST_12_MONTH_AVG"],
        row["PRECENTAGE_CHANGE_FROM_LAST_12MONTHS_AVG"],
        row["WARNING_THRESHOLD"],
        row["highlight"],
      ),
    )
    setRows(temp) // Update the rows state with transformed data
  }, [data])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value) // Update rows per page
    setPage(0) // Reset to the first page
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 515 }}>
        <Table stickyHeader aria-label="sticky table">
          {/* Render table header and rows */}
          <TableHeader classes={classes} />
          <TableRows
            rows={rows}
            page={page}
            rowsPerPage={rowsPerPage}
            classes={classes}
          />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20]}
        component="div"
        count={rows.length} // Total number of rows
        rowsPerPage={rowsPerPage} // Current number of rows displayed
        page={page} // Current page
        onPageChange={handleChangePage} // Function to handle page changes
        onRowsPerPageChange={handleChangeRowsPerPage} // Function to handle changing the number of rows per page
      />
    </Paper>
  )
}
